import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  displayName: string | null;
  avatarId: number | null;
  needsOnboarding: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateDisplayName: (name: string) => Promise<void>;
  updateAvatar: (id: number | null) => Promise<void>;
  skipAvatar: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Return safe defaults when outside AuthProvider (shouldn't happen in production)
    return {
      user: null,
      session: null,
      loading: true,
      displayName: null,
      avatarId: null,
      needsOnboarding: false,
      signInWithGoogle: async () => {},
      signInWithMagicLink: async () => ({ error: null }),
      signUp: async () => ({ error: null }),
      signInWithPassword: async () => ({ error: null }),
      signOut: async () => {},
      updateDisplayName: async () => {},
      updateAvatar: async () => {},
      skipAvatar: async () => {},
      completeOnboarding: async () => {},
    } as AuthContextType;
  }
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const displayName = user?.user_metadata?.display_name || user?.user_metadata?.full_name || null;
  const avatarId: number | null = user?.user_metadata?.avatar_id ?? null;

  const onboardingCompleted = !!user?.user_metadata?.onboarding_completed;
  // Single unified flag — any new user (onboarding_completed not yet set) will see the unified onboarding flow
  const needsOnboarding = !!user && !onboardingCompleted;

  const initializedRef = useRef(false);

  useEffect(() => {
    // Strict Mode guard — only run once
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Check for auth errors in URL (e.g. otp_expired from failed PKCE)
    const params = new URLSearchParams(window.location.search);
    const errorCode = params.get('error_code');
    if (errorCode) {
      // Clean error params from URL
      window.history.replaceState({}, '', '/');
      // Dispatch a custom event so LoginPage can show the toast
      window.dispatchEvent(new CustomEvent('auth-url-error', { detail: { errorCode } }));
    }

    // Let Supabase client handle PKCE code exchange automatically
    // via detectSessionInUrl: true — no manual exchangeCodeForSession needed
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Clean code/token params from URL after successful auth
      if (session && (params.has('code') || window.location.hash)) {
        const pendingPath = localStorage.getItem('pendingInvitePath');
        const postAuthRedirect = localStorage.getItem('postAuthRedirect');
        if (pendingPath) {
          // Do NOT remove pendingInvitePath here — let it survive through onboarding
          // so UnifiedOnboarding can read it. JoinGroup clears it after processing.
          // The one-shot postAuthRedirect is moot once invite wins — drop it.
          localStorage.removeItem('postAuthRedirect');
          window.history.replaceState({}, '', pendingPath);
        } else if (postAuthRedirect && window.location.pathname === postAuthRedirect) {
          // OAuth/magic-link return for a one-shot destination (e.g. /feedback):
          // honor it only when we actually landed on that page. Consume here so a
          // later unrelated login can't bounce there.
          localStorage.removeItem('postAuthRedirect');
          window.history.replaceState({}, '', postAuthRedirect);
        } else {
          // No active destination (or a stale postAuthRedirect from an abandoned
          // visit) — clear any stale value and fall back home.
          localStorage.removeItem('postAuthRedirect');
          window.history.replaceState({}, '', '/');
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Where to land after auth. pendingInvitePath wins (invite flow), then a
  // one-shot postAuthRedirect (e.g. /feedback) — but only when the sign-in is
  // actually initiated from that page, so a stale value can't hijack an
  // unrelated OAuth/magic-link/sign-up login. Then home.
  const authRedirectPath = () => {
    const invite = localStorage.getItem('pendingInvitePath');
    if (invite) return invite;
    const postAuth = localStorage.getItem('postAuthRedirect');
    if (postAuth && window.location.pathname === postAuth) return postAuth;
    return '/';
  };

  const signInWithGoogle = async () => {
    const pendingPath = authRedirectPath();
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}${pendingPath}` },
    });
  };

  const signInWithMagicLink = async (email: string) => {
    const pendingPath = authRedirectPath();
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}${pendingPath}` },
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const pendingPath = authRedirectPath();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}${pendingPath}` },
    });
    return { error: error as Error | null };
  };

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const updateDisplayName = async (name: string) => {
    const { data, error } = await supabase.auth.updateUser({
      data: { display_name: name },
    });
    if (!error && data.user) {
      setUser(data.user);
      // Sync display_name across all denormalized tables
      const uid = data.user.id;
      await Promise.all([
        supabase.from('group_members').update({ display_name: name }).eq('user_id', uid),
        supabase.from('leaderboard').update({ display_name: name }).eq('user_id', uid),
        supabase.from('global_leaderboard').update({ display_name: name }).eq('user_id', uid),
        supabase.from('bracket_entries').update({ display_name: name }).eq('user_id', uid),
      ]);
    }
  };

  const updateAvatar = async (id: number | null) => {
    const { data, error } = await supabase.auth.updateUser({
      data: { avatar_id: id },
    });
    if (!error && data.user) {
      setUser(data.user);
      // Sync avatar_id to all group_members rows for this user
      if (data.user.id) {
        await supabase
          .from('group_members')
          .update({ avatar_id: id })
          .eq('user_id', data.user.id);
      }
    }
  };

  const skipAvatar = async () => {
    const { data, error } = await supabase.auth.updateUser({
      data: { avatar_skipped: true },
    });
    if (!error && data.user) {
      setUser(data.user);
    }
  };

  const completeOnboarding = async () => {
    const { data, error } = await supabase.auth.updateUser({
      data: { onboarding_completed: true },
    });
    if (!error && data.user) setUser(data.user);
  };

  const value = useMemo(() => ({
    user,
    session,
    loading,
    displayName,
    avatarId,
    needsOnboarding,
    signInWithGoogle,
    signInWithMagicLink,
    signUp,
    signInWithPassword,
    signOut,
    updateDisplayName,
    updateAvatar,
    skipAvatar,
    completeOnboarding,
  }), [user, session, loading, displayName, avatarId, needsOnboarding,
       signInWithGoogle, signInWithMagicLink, signUp, signInWithPassword,
       signOut, updateDisplayName, updateAvatar, skipAvatar, completeOnboarding]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
